import React, { useState, useEffect, useRef } from 'react';
import './ArcaAgentWidget.css';

const ArcaAgentWidget = ({ autoOpen = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isGreeted, setIsGreeted] = useState(false);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  const CFG = {
    endpoint: "/api/arca-agent",
    mock: false,
    orgName: "Africa Respiratory Care Association",
    agentName: "ARCA",
    contactEmail: "support@africaresp.org",
    requestTimeoutMs: 15000,
    colors: {
      primary: "#6B8E23",
      primaryDark: "#58761C",
      darkGray: "#4A4F4F",
      textOnPrimary: "#fff"
    },
    logoUrl: process.env.REACT_APP_LOGO_URL || undefined
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages]);

  // Auto-open widget and greet user (only if autoOpen is true)
  useEffect(() => {
    if (autoOpen) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        greetUser();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [autoOpen]);

  // Focus input when widget opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const greetUser = () => {
    if (!isGreeted) {
      setIsGreeted(true);
      addMessage('bot', `Hello! I'm ${CFG.agentName}, the AI assistant of the ${CFG.orgName}.\n\nHeartfelt greetings from our President & Founder Dorothy H. Bendah. Thank you for your interest in ARCA.`);
    }
  };

  const addMessage = (role, text) => {
    setMessages(prev => [...prev, { id: Date.now(), role, text }]);
  };

  const sendToBackend = async (message) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (!backendUrl) {
        console.error('Backend URL not configured');
        return;
      }
      console.log('Sending message to backend:', `${backendUrl}/api/arca-agent/chat`);

      const response = await fetch(`${backendUrl}/api/arca-agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message }),
      });

      console.log('Backend response status:', response.status);

      const data = await response.json();

      if (data.success) {
        addMessage('bot', data.response);
      } else {
        addMessage('bot', "Sorry, I encountered an error processing your request. Please try again.");
      }
    } catch (error) {
      console.error('Error sending message to backend:', error);
      addMessage('bot', "Sorry, I'm having trouble connecting to the server. Please try again later.");
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMessage = inputValue.trim();
    addMessage('user', userMessage);
    setInputValue('');

    // Send to backend for processing
    await sendToBackend(userMessage);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  const toggleWidget = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      greetUser();
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <button
        className="arca-fab"
        onClick={toggleWidget}
        aria-label="Open ARCA AI Assistant"
      >
        💬
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <section className="arca-panel open">
          <header className="arca-header">
            <div className="arca-brand">
              {CFG.logoUrl && <img className="arca-logo" src={CFG.logoUrl} alt="ARCA Logo" />}
              <div className="arca-title">Ask {CFG.agentName}</div>
            </div>
            <button
              className="arca-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              ×
            </button>
          </header>

          <div className="arca-welcome">
            Welcome to ARCA. Together, we advance respiratory care in Africa.
          </div>

          <main className="arca-body" ref={bodyRef}>
            {messages.map((msg) => (
              <div key={msg.id} className={`arca-msg ${msg.role}`}>
                <div className={`arca-bubble ${msg.role === 'user' ? 'user' : 'bot'}`}>
                  {msg.text}
                </div>
              </div>
            ))}
          </main>

          <footer className="arca-input">
            <input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              autoComplete="off"
              aria-label="Type your message"
            />
            <button
              onClick={handleSend}
              aria-label="Send message"
            >
              Send
            </button>
          </footer>

          <div className="arca-note">
            {CFG.agentName} may make mistakes. Verify critical info.
          </div>
        </section>
      )}
    </>
  );
};

export default ArcaAgentWidget;
